import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../lib/api';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import { motion } from 'framer-motion';
import { FileText, Download, ChevronLeft, ChevronRight, DollarSign, Receipt, Percent, TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight, Scale } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';

const taxCategories = {
  work_expenses: 'Work-related Expenses', home_office: 'Home Office Expenses', vehicle: 'Vehicle & Travel',
  uniform: 'Uniform, Clothing & Laundry', tools: 'Tools & Equipment', education: 'Self-education Expenses',
  donations: 'Gifts & Donations', medical: 'Medical Expenses', investment: 'Investment Expenses',
};

const fmt = (n) => new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(n || 0);

export default function TaxReports() {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const { data: receipts = [] } = useQuery({ queryKey: ['receipts'], queryFn: async () => { const { data } = await api.get('/api/receipts?sort=-date&limit=500'); return data; } });
  const { data: invoices = [] } = useQuery({ queryKey: ['invoices'], queryFn: async () => { const { data } = await api.get('/api/invoices'); return data; } });

  // Australian financial year: July 1 to June 30
  const fyStart = new Date(selectedYear - 1, 6, 1);
  const fyEnd = new Date(selectedYear, 5, 30, 23, 59, 59);

  // Expenses (Receipts)
  const fyReceipts = receipts.filter(r => { const d = new Date(r.date); return d >= fyStart && d <= fyEnd; });
  const totalSpent = fyReceipts.reduce((s, r) => s + (r.amount || 0), 0);
  const deductibleReceipts = fyReceipts.filter(r => r.tax_deductible);
  const totalDeductible = deductibleReceipts.reduce((s, r) => s + ((r.amount || 0) * (r.tax_split_percentage || 100) / 100), 0);
  const gstReceipts = fyReceipts.filter(r => r.gst_type === 'gst_10');
  const gstPaid = gstReceipts.reduce((s, r) => s + (r.gst_amount || r.amount / 11), 0);
  const businessReceipts = fyReceipts.filter(r => r.is_business_expense);
  const totalBusiness = businessReceipts.reduce((s, r) => s + (r.amount || 0), 0);

  // Income (Invoices)
  const fyInvoices = invoices.filter(inv => {
    const d = new Date(inv.issue_date || inv.paid_date || inv.created_at);
    return d >= fyStart && d <= fyEnd;
  });
  const paidInvoices = fyInvoices.filter(inv => inv.status === 'paid');
  const totalIncome = paidInvoices.reduce((s, inv) => s + (inv.total || 0), 0);
  const totalIncomeSubtotal = paidInvoices.reduce((s, inv) => s + (inv.subtotal || 0), 0);
  const gstCollected = paidInvoices.reduce((s, inv) => s + (inv.gst_amount || 0), 0);
  const outstandingInvoices = fyInvoices.filter(inv => inv.status !== 'paid' && inv.status !== 'cancelled');
  const totalOutstanding = outstandingInvoices.reduce((s, inv) => s + (inv.total || 0), 0);

  // Net GST
  const netGST = gstCollected - gstPaid;

  // Group by tax category
  const byTaxCategory = deductibleReceipts.reduce((acc, r) => {
    const cat = r.tax_category || 'work_expenses';
    if (!acc[cat]) acc[cat] = { count: 0, total: 0, receipts: [] };
    const amt = (r.amount || 0) * (r.tax_split_percentage || 100) / 100;
    acc[cat].total += amt;
    acc[cat].count++;
    acc[cat].receipts.push(r);
    return acc;
  }, {});

  // Monthly breakdown (expenses + income)
  const monthlyData = Array.from({ length: 12 }, (_, i) => {
    const month = new Date(selectedYear - 1, 6 + i);
    const ms = startOfMonth(month);
    const me = endOfMonth(month);
    const mr = fyReceipts.filter(r => { const d = new Date(r.date); return d >= ms && d <= me; });
    const mi = paidInvoices.filter(inv => { const d = new Date(inv.issue_date || inv.paid_date || inv.created_at); return d >= ms && d <= me; });
    return {
      month: format(month, 'MMM yyyy'),
      expenses: mr.reduce((s, r) => s + (r.amount || 0), 0),
      deductible: mr.filter(r => r.tax_deductible).reduce((s, r) => s + ((r.amount || 0) * (r.tax_split_percentage || 100) / 100), 0),
      gstPaid: mr.filter(r => r.gst_type === 'gst_10').reduce((s, r) => s + (r.gst_amount || r.amount / 11), 0),
      income: mi.reduce((s, inv) => s + (inv.total || 0), 0),
      gstCollected: mi.reduce((s, inv) => s + (inv.gst_amount || 0), 0),
    };
  });

  const generateCSV = () => {
    const headers = ['Date', 'Type', 'Description', 'Amount', 'GST', 'Category', 'Tax Deductible', 'Split %'];
    const expenseRows = deductibleReceipts.map(r => [r.date, 'Expense', r.store_name, r.amount, r.gst_amount || '', r.tax_category, 'Yes', r.tax_split_percentage]);
    const incomeRows = paidInvoices.map(inv => [inv.issue_date || inv.paid_date, 'Income', inv.client_name, inv.total, inv.gst_amount, 'income', '', '']);
    const csv = [headers, ...expenseRows, ...incomeRows].map(r => r.map(c => `"${c || ''}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `tax-report-fy${selectedYear}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="tax-reports-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div><h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Tax Reports</h1><p className="text-slate-500 mt-1">ATO-ready BAS summaries for your tax return</p></div>
          <div className="flex gap-3">
            <Button onClick={generateCSV} variant="outline" data-testid="export-tax-report"><Download className="w-4 h-4 mr-2" />Export CSV</Button>
            <Button onClick={() => { const token = localStorage.getItem('access_token'); window.open(`${process.env.REACT_APP_BACKEND_URL}/api/export/tax-report?year=${selectedYear}&token=${token}`, '_blank'); }} variant="outline" data-testid="export-tax-pdf"><Download className="w-4 h-4 mr-2" />Export PDF</Button>
          </div>
        </div>

        {/* Financial Year Selector */}
        <div className="flex items-center justify-center gap-4 mb-8">
          <Button variant="ghost" size="icon" onClick={() => setSelectedYear(y => y - 1)}><ChevronLeft className="w-5 h-5" /></Button>
          <h2 className="text-xl font-semibold text-slate-900 min-w-[200px] text-center">FY {selectedYear - 1}/{selectedYear}</h2>
          <Button variant="ghost" size="icon" onClick={() => setSelectedYear(y => y + 1)}><ChevronRight className="w-5 h-5" /></Button>
        </div>

        {/* ═══ GST Summary (BAS-style) ═══ */}
        <div className="bg-white rounded-2xl border border-slate-100 p-6 mb-6" data-testid="gst-summary-section">
          <div className="flex items-center gap-2 mb-5">
            <Scale className="w-5 h-5 text-slate-700" />
            <h3 className="text-lg font-bold text-slate-900">GST Summary</h3>
            <span className="text-xs text-slate-400 ml-1">(BAS Activity Statement)</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* GST Collected */}
            <div className="bg-blue-50 rounded-xl p-5 border border-blue-100">
              <div className="flex items-center gap-2 mb-1">
                <ArrowDownRight className="w-4 h-4 text-blue-600" />
                <p className="text-xs text-blue-600 font-semibold uppercase tracking-wider">GST Collected</p>
              </div>
              <p className="text-sm text-slate-500 mb-2">From invoices (income)</p>
              <p className="text-2xl font-bold text-blue-700">{fmt(gstCollected)}</p>
              <p className="text-xs text-blue-500 mt-1">{paidInvoices.length} paid invoice{paidInvoices.length !== 1 ? 's' : ''}</p>
            </div>
            {/* GST Paid */}
            <div className="bg-purple-50 rounded-xl p-5 border border-purple-100">
              <div className="flex items-center gap-2 mb-1">
                <ArrowUpRight className="w-4 h-4 text-purple-600" />
                <p className="text-xs text-purple-600 font-semibold uppercase tracking-wider">GST Paid</p>
              </div>
              <p className="text-sm text-slate-500 mb-2">From expenses (purchases)</p>
              <p className="text-2xl font-bold text-purple-700">{fmt(gstPaid)}</p>
              <p className="text-xs text-purple-500 mt-1">{gstReceipts.length} receipt{gstReceipts.length !== 1 ? 's' : ''} with GST</p>
            </div>
            {/* Net GST */}
            <div className={`${netGST >= 0 ? 'bg-amber-50 border-amber-100' : 'bg-emerald-50 border-emerald-100'} rounded-xl p-5 border`}>
              <div className="flex items-center gap-2 mb-1">
                <Scale className={`w-4 h-4 ${netGST >= 0 ? 'text-amber-600' : 'text-emerald-600'}`} />
                <p className={`text-xs font-semibold uppercase tracking-wider ${netGST >= 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                  {netGST >= 0 ? 'GST Payable to ATO' : 'GST Refund from ATO'}
                </p>
              </div>
              <p className="text-sm text-slate-500 mb-2">Collected minus paid</p>
              <p className={`text-2xl font-bold ${netGST >= 0 ? 'text-amber-700' : 'text-emerald-700'}`}>{fmt(Math.abs(netGST))}</p>
              <p className="text-xs text-slate-400 mt-1">{fmt(gstCollected)} - {fmt(gstPaid)}</p>
            </div>
          </div>
        </div>

        {/* ═══ Income & Expense Summary ═══ */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          {[
            { label: 'Total Income', value: fmt(totalIncome), sub: `${paidInvoices.length} invoices`, icon: TrendingUp, bg: 'bg-emerald-50', color: 'text-emerald-600', border: 'border-emerald-100' },
            { label: 'Total Expenses', value: fmt(totalSpent), sub: `${fyReceipts.length} receipts`, icon: TrendingDown, bg: 'bg-red-50', color: 'text-red-600', border: 'border-red-100' },
            { label: 'Tax Deductible', value: fmt(totalDeductible), sub: `${deductibleReceipts.length} items`, icon: Percent, bg: 'bg-blue-50', color: 'text-blue-600', border: 'border-blue-100' },
            { label: 'Business Expenses', value: fmt(totalBusiness), sub: `${businessReceipts.length} items`, icon: Receipt, bg: 'bg-purple-50', color: 'text-purple-600', border: 'border-purple-100' },
            { label: 'Outstanding', value: fmt(totalOutstanding), sub: `${outstandingInvoices.length} invoices`, icon: DollarSign, bg: 'bg-amber-50', color: 'text-amber-600', border: 'border-amber-100' },
          ].map(stat => (
            <motion.div key={stat.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className={`${stat.bg} rounded-2xl p-5 border ${stat.border}`}>
              <stat.icon className={`w-5 h-5 ${stat.color} mb-2`} />
              <p className="text-xs text-slate-500 uppercase tracking-wider">{stat.label}</p>
              <p className="text-xl font-bold text-slate-900 mt-1">{stat.value}</p>
              <p className="text-xs text-slate-400 mt-0.5">{stat.sub}</p>
            </motion.div>
          ))}
        </div>

        <Tabs defaultValue="gst">
          <TabsList className="w-full bg-slate-100 mb-6">
            <TabsTrigger value="gst" className="flex-1">GST Monthly</TabsTrigger>
            <TabsTrigger value="categories" className="flex-1">By Tax Category</TabsTrigger>
            <TabsTrigger value="income" className="flex-1">Income Detail</TabsTrigger>
          </TabsList>

          {/* ═══ GST Monthly Tab ═══ */}
          <TabsContent value="gst" className="space-y-3" data-testid="tax-gst-tab">
            {/* Header */}
            <div className="bg-slate-900 rounded-xl p-4 flex items-center justify-between text-xs font-medium text-slate-400 uppercase tracking-wider">
              <span className="w-28">Month</span>
              <div className="flex gap-6 text-right">
                <span className="w-24">Income</span>
                <span className="w-24">GST Collected</span>
                <span className="w-24">Expenses</span>
                <span className="w-24">GST Paid</span>
                <span className="w-24">Net GST</span>
              </div>
            </div>
            {monthlyData.map((m, i) => {
              const net = m.gstCollected - m.gstPaid;
              return (
                <div key={i} className="bg-white rounded-xl border border-slate-100 p-4 flex items-center justify-between">
                  <p className="font-semibold text-slate-900 w-28">{m.month}</p>
                  <div className="flex gap-6 text-right">
                    <div className="w-24"><p className="font-semibold text-emerald-600">{fmt(m.income)}</p></div>
                    <div className="w-24"><p className="font-semibold text-blue-600">{fmt(m.gstCollected)}</p></div>
                    <div className="w-24"><p className="font-semibold text-red-600">{fmt(m.expenses)}</p></div>
                    <div className="w-24"><p className="font-semibold text-purple-600">{fmt(m.gstPaid)}</p></div>
                    <div className="w-24"><p className={`font-bold ${net >= 0 ? 'text-amber-600' : 'text-emerald-600'}`}>{net >= 0 ? '' : '-'}{fmt(Math.abs(net))}</p></div>
                  </div>
                </div>
              );
            })}
            {/* Totals */}
            <div className="bg-slate-900 rounded-xl p-4 flex items-center justify-between text-white">
              <p className="font-bold w-28">TOTAL</p>
              <div className="flex gap-6 text-right">
                <div className="w-24"><p className="font-bold text-emerald-400">{fmt(totalIncome)}</p></div>
                <div className="w-24"><p className="font-bold text-blue-400">{fmt(gstCollected)}</p></div>
                <div className="w-24"><p className="font-bold text-red-400">{fmt(totalSpent)}</p></div>
                <div className="w-24"><p className="font-bold text-purple-400">{fmt(gstPaid)}</p></div>
                <div className="w-24"><p className={`font-bold ${netGST >= 0 ? 'text-amber-400' : 'text-emerald-400'}`}>{netGST >= 0 ? '' : '-'}{fmt(Math.abs(netGST))}</p></div>
              </div>
            </div>
          </TabsContent>

          {/* ═══ Categories Tab ═══ */}
          <TabsContent value="categories" className="space-y-4" data-testid="tax-categories-tab">
            {Object.entries(byTaxCategory).map(([cat, data]) => (
              <div key={cat} className="bg-white rounded-xl border border-slate-100 p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-slate-900">{taxCategories[cat] || cat}</h3>
                  <div className="text-right"><p className="font-bold text-slate-900">{fmt(data.total)}</p><p className="text-xs text-slate-500">{data.count} receipts</p></div>
                </div>
                <div className="space-y-2">
                  {data.receipts.slice(0, 3).map(r => (
                    <div key={r.id} className="flex items-center justify-between text-sm bg-slate-50 rounded-lg p-2.5">
                      <div><span className="font-medium text-slate-900">{r.store_name}</span><span className="text-slate-400 ml-2">{r.date && format(new Date(r.date), 'dd/MM/yy')}</span></div>
                      <span className="font-medium text-slate-900">{fmt(r.amount)}{r.tax_split_percentage < 100 && <span className="text-xs text-slate-400 ml-1">({r.tax_split_percentage}%)</span>}</span>
                    </div>
                  ))}
                  {data.receipts.length > 3 && <p className="text-xs text-slate-400 text-center py-1">+{data.receipts.length - 3} more receipts</p>}
                </div>
              </div>
            ))}
            {Object.keys(byTaxCategory).length === 0 && <div className="text-center py-16 bg-white rounded-2xl border border-slate-100"><FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" /><h3 className="text-lg font-medium text-slate-900 mb-2">No tax-deductible expenses</h3><p className="text-slate-500">Mark receipts as tax deductible to see them here</p></div>}
          </TabsContent>

          {/* ═══ Income Detail Tab ═══ */}
          <TabsContent value="income" className="space-y-4" data-testid="tax-income-tab">
            <div className="bg-emerald-50 rounded-xl border border-emerald-100 p-5 mb-2">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-emerald-700">Total Income (ex. GST)</p>
                  <p className="text-2xl font-bold text-emerald-800 mt-1">{fmt(totalIncomeSubtotal)}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold text-blue-700">GST Collected</p>
                  <p className="text-2xl font-bold text-blue-800 mt-1">{fmt(gstCollected)}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold text-slate-700">Total (inc. GST)</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">{fmt(totalIncome)}</p>
                </div>
              </div>
            </div>
            {paidInvoices.length > 0 ? paidInvoices.map(inv => (
              <div key={inv.id} className="bg-white rounded-xl border border-slate-100 p-4 flex items-center justify-between">
                <div>
                  <p className="font-semibold text-slate-900">{inv.client_name}</p>
                  <p className="text-sm text-slate-500">{inv.invoice_number} {inv.issue_date && `· ${format(new Date(inv.issue_date), 'dd MMM yyyy')}`}</p>
                </div>
                <div className="flex items-center gap-6 text-right">
                  <div><p className="text-xs text-slate-400">Subtotal</p><p className="font-medium text-slate-700">{fmt(inv.subtotal)}</p></div>
                  <div><p className="text-xs text-slate-400">GST</p><p className="font-medium text-blue-600">{fmt(inv.gst_amount)}</p></div>
                  <div><p className="text-xs text-slate-400">Total</p><p className="font-bold text-slate-900">{fmt(inv.total)}</p></div>
                  <span className="px-2.5 py-1 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-full">Paid</span>
                </div>
              </div>
            )) : (
              <div className="text-center py-16 bg-white rounded-2xl border border-slate-100">
                <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">No paid invoices this FY</h3>
                <p className="text-slate-500">Create and mark invoices as paid to track income here</p>
              </div>
            )}
            {outstandingInvoices.length > 0 && (
              <>
                <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mt-6">Outstanding Invoices</h4>
                {outstandingInvoices.map(inv => (
                  <div key={inv.id} className="bg-white rounded-xl border border-amber-100 p-4 flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-slate-900">{inv.client_name}</p>
                      <p className="text-sm text-slate-500">{inv.invoice_number} {inv.due_date && `· Due ${format(new Date(inv.due_date), 'dd MMM yyyy')}`}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <p className="font-bold text-amber-700">{fmt(inv.total)}</p>
                      <span className="px-2.5 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded-full capitalize">{inv.status}</span>
                    </div>
                  </div>
                ))}
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
